import { useState } from 'react';
import { SupportingDocumentFile } from '../../../../../features/gms-proposal-forms/payment_term-loan_maturity-commission_services-notes-files/payment_term-loan_maturity-commission_services-notes-files.state';
import { getFileIcon } from '../../../../ui/get-file-icon';

interface Props {
    handleDownload: (fileName: string, filePath: string) => void;
    files: SupportingDocumentFile[];
    title: string;
    subtitle: string;
    isLoading?: boolean;
}

export default function ProposalPreviewDocuments({ isLoading = false, files, handleDownload, title, subtitle }: Props) {
    const [isOpen, setIsOpen] = useState(false);

    if (files.length === 0) return null;

    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            <button 
                type="button" 
                onClick={() => setIsOpen(prev => !prev)} 
                className={`${isOpen ? 'border-b' : ''} flex w-full items-center gap-2 border-gray-200 p-2.5 text-left transition hover:bg-gray-50 sm:p-3`}
                aria-expanded={isOpen}
            >
                <div className="border border-[#186cb2]/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#186cb2]">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8" />
                        <path d="M8 17h5" />
                    </svg>
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <p className="text-[11px] font-semibold text-[#186cb2]">{title}</p>
                        <span className="text-[11px] font-semibold text-[#186cb2]">{`(${files.length})`}</span>
                    </div>
                    <p className="truncate text-[10px] text-gray-500">{subtitle}</p>
                </div>

                <svg className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </button>

            {isOpen && (
                <div className="space-y-0 p-2.5 sm:p-3">
                    {files.map((file, index) => (
                        <div key={`${file.id}-${index}`} className={`flex min-w-0 items-center gap-2 bg-gray-50/70 p-1.5 transition hover:bg-[#186cb2]/5 ${index !== files.length - 1 ? 'border-b border-gray-200' : ''}`}>
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white shadow-sm">
                                {getFileIcon(file.fileName)}
                            </div>

                            <div className="min-w-0 flex-1">
                                <p className="truncate text-xs font-medium text-gray-700">
                                    {file.fileName}
                                </p>
                                <p className="text-[9px] text-gray-400">
                                    Supporting document
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => handleDownload(file.filePath, file.fileName)}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#186cb2]/20 bg-white text-[#186cb2] transition active:scale-95"
                                title={`Download ${file.fileName}`}
                                aria-label={`Download ${file.fileName}`}
                            >
                                {isLoading ? (
                                    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 animate-spin" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" className="opacity-25" /><path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                )}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}